import { useState, useEffect, useCallback, useReducer, useRef } from "react";
import { Button, Dropdown, Layout } from "antd";
import ChatResults from "../../components/Chat/ChatResults";
import ChatSources from "../../components/Chat/ChatSources";
import ChatInput from "../../components/Chat/ChatInput";
import ChatCover from "../../components/Chat/ChatCover";
import ChatHeader from "../../components/Chat/ChatHeader";
import CreateInsightPanel from "../../components/Insights/CreateInsightPanel";
import { useLocation } from "react-router";
import GuidelinesModal from "../../components/Chat/GuidelinesModal";
import { stripContextPrefix } from "../../components/AskVivo/askVivo.utils";
import AskVivoDeleteConfirmModal from "../../components/AskVivo/AskVivoDeleteConfirmModal";
import AskVivoHistoryPanel from "../../components/AskVivo/AskVivoHistoryPanel";
import SidePanelShell from "../../components/AskVivo/SidePanelShell";
import { useDontAskAgain } from "../../utils/useDontAskAgain";
import { useAskVivo } from "../../contexts/AskVivoContext";
import { useAskVivoHistory } from "../../hooks/useAskVivoHistory";
import { useHistoryBulkDelete } from "../../hooks/useHistoryBulkDelete";
import { useAppNotification } from "../../hooks/useAppNotification";
import DeleteIcon from "../../assets/icons/delete.svg?react";
import {
  askVivoLocalReducer,
  INITIAL_LOCAL_STATE,
} from "../../components/AskVivo/askVivoLocalReducer";
import {
  AV_ERROR_DESCRIPTION_TEXT,
  AV_ERROR_TEXT,
} from "../../components/AskVivo/askVivo.constants";
import type { ChatMessage, SelectedSources } from "../../components/Chat/chat";

const { Content, Header, Footer, Sider } = Layout;

export default function AskVivo() {
  const location = useLocation();

  const insightsQuestion = location.state?.question;
  const askVivoNewSessionToken = location.state?.askVivoNewSessionToken;
  const handledInsightsQuestionRef = useRef<string | null>(null);
  const handledAskVivoNewSessionTokenRef = useRef<string | null>(null);

  const {
    chatHistory,
    sessionId,
    isChatLoading,
    sendMessage,
    resetSession,
    loadSession,
    panel,
    openPanel,
    closePanel,
  } = useAskVivo();

  const {
    historyData,
    isHistoryLoading,
    historyError,
    fetchHistory,
    deleteHistory,
    upsertHistoryItem,
    loadSession: loadHistorySession,
  } = useAskVivoHistory();

  const { openErrorNotification, contextHolder } = useAppNotification();

  const [localState, dispatch] = useReducer(
    askVivoLocalReducer,
    INITIAL_LOCAL_STATE,
  );
  const {
    isNewSession,
    contextPrompt,
    oldSessionTimeStamp,
    firstChat,
    isAHistoryItem,
  } = localState;

  useEffect(() => {
    if (isNewSession) {
      resetSession();
    }
  }, [isNewSession, resetSession]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { dontAskAgain, setDontAskAgain } = useDontAskAgain(
    "askVivo.skipDeleteConfirm",
  );
  const bulkDelete = useHistoryBulkDelete();

  const notifyDeleteError = useCallback((): void => {
    openErrorNotification({
      title: (
        <p className="text-base font-semibold" style={{ color: AV_ERROR_TEXT }}>
          Delete failed
        </p>
      ),
      description: (
        <p className="text-[14px]" style={{ color: AV_ERROR_DESCRIPTION_TEXT }}>
          There was an error deleting this session. Please try again.
        </p>
      ),
      placement: "bottom",
      className: "insight-error-confirmation",
      duration: 3,
      icon: null,
    });
  }, [openErrorNotification]);

  const startNewSession = useCallback((): string => {
    dispatch({ type: "RESET" });
    const newSessionId = resetSession();
    bulkDelete.resetBulkDelete();
    closePanel();
    return newSessionId;
  }, [bulkDelete, closePanel, resetSession]);

  const handleViewSessionHistory = (): void => {
    bulkDelete.resetBulkDelete();
    openPanel({ type: "history" });
    fetchHistory();
  };

  const handleViewSearchSources = (
    chat: ChatMessage | undefined,
    chatId: number,
  ): void => {
    if (!chat) return;
    openPanel({ type: "sources", chat, chatId });
  };

  const handleCloseSessionHistory = (): void => {
    bulkDelete.resetBulkDelete();
    closePanel();
  };

  const handleCloseSearchSources = (): void => closePanel();

  const handleOpenCreateInsights = (chat?: ChatMessage): void => {
    openPanel({
      type: "insights",
      insight: chat ?? firstChat ?? null,
      step: "create",
    });
  };

  const handleSearch = useCallback(
    (userPrompt: string, overrideSessionId?: string): void => {
      if (!userPrompt?.trim()) return;
      dispatch({
        type: "SEARCH_STARTED",
        prompt: userPrompt,
        isFirstMessage: chatHistory.length === 0,
      });
      if (panel.type === "sources") closePanel();
      sendMessage(
        userPrompt,
        overrideSessionId ? { sessionId: overrideSessionId } : undefined,
      );
    },
    [chatHistory.length, closePanel, panel.type, sendMessage],
  );

  const wasChatLoadingRef = useRef(false);
  useEffect(() => {
    if (wasChatLoadingRef.current && !isChatLoading && chatHistory.length > 0) {
      const latestChat = chatHistory[0];
      dispatch({
        type: "CHAT_COMPLETED",
        firstChat: latestChat,
        contextPrompt: stripContextPrefix(latestChat.input ?? ""),
      });
    }
    wasChatLoadingRef.current = isChatLoading;
  }, [chatHistory, isChatLoading]);

  // Sync the session's opening chat into the cached history list, which shows
  // one entry per session. Keyed off the chat itself rather than the end of
  // loading: the response and the isPending flip are separate state updates, so
  // on the render where loading ends the in-flight placeholder may not have
  // been replaced yet — and that placeholder has no timestamp, so upserting it
  // pins an entry with no usable date in the panel.
  const upsertedSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    const openingChat = chatHistory[0];
    if (!openingChat?.session_id) return;
    if (
      openingChat.isLoading ||
      openingChat.hasError ||
      !openingChat.timestamp
    ) {
      return;
    }
    if (upsertedSessionIdRef.current === openingChat.session_id) return;

    upsertedSessionIdRef.current = openingChat.session_id;
    upsertHistoryItem(openingChat);
  }, [chatHistory, upsertHistoryItem]);

  useEffect(() => {
    if (!insightsQuestion) {
      handledInsightsQuestionRef.current = null;
      return;
    }
    if (handledInsightsQuestionRef.current === insightsQuestion) return;
    handledInsightsQuestionRef.current = insightsQuestion;
    const newSessionId = startNewSession();
    handleSearch(insightsQuestion, newSessionId);
  }, [handleSearch, insightsQuestion, startNewSession]);

  useEffect(() => {
    if (!askVivoNewSessionToken) {
      handledAskVivoNewSessionTokenRef.current = null;
      return;
    }
    if (handledAskVivoNewSessionTokenRef.current === askVivoNewSessionToken) {
      return;
    }
    handledAskVivoNewSessionTokenRef.current = askVivoNewSessionToken;
    startNewSession();
  }, [askVivoNewSessionToken, startNewSession]);

  const handleRegenerate = (
    existingSessionId?: string,
    userPrompt?: string,
    selectedSources?: SelectedSources,
  ): void => {
    if (!userPrompt) return;
    dispatch({ type: "REGENERATE_STARTED" });
    if (panel.type === "sources") closePanel();
    sendMessage(userPrompt, {
      sessionId: existingSessionId,
      selectedSources,
    });
  };

  const handleLoadHistoryItem = async (
    historySessionId: string,
  ): Promise<void> => {
    try {
      const data = await loadHistorySession(historySessionId);
      if (!data.length) return;
      loadSession(data, data[0].session_id ?? "");
      dispatch({
        type: "SESSION_LOADED",
        firstChat: data[0],
        contextPrompt: stripContextPrefix(data[0].input ?? ""),
        oldSessionTimeStamp: data[0].timestamp ?? "",
      });
    } catch (error) {
      console.error(error);
      dispatch({ type: "SESSION_LOAD_FAILED" });
    }
  };

  const handleSuggestedQuestions = (question: string): void => {
    handleSearch(question);
  };

  const handleCloseInsightsPanel = (): void => {
    closePanel();
  };

  const handleOpenModal = (): void => setIsModalOpen(true);

  const handleDeleteHistoryItem = useCallback(
    async (historySessionId: string): Promise<void> => {
      try {
        await deleteHistory(historySessionId);
        if (historySessionId === sessionId) {
          startNewSession();
        }
      } catch (error) {
        console.error("error deleting session", error);
        notifyDeleteError();
      }
    },
    [deleteHistory, notifyDeleteError, sessionId, startNewSession],
  );

  const handleDeleteCurrentSession = useCallback((): Promise<void> => {
    return handleDeleteHistoryItem(sessionId);
  }, [handleDeleteHistoryItem, sessionId]);

  const handleDeleteMultipleHistoryItems = useCallback(
    async (sessionIds: string[]): Promise<void> => {
      try {
        await Promise.all(sessionIds.map((id) => deleteHistory(id)));
        if (sessionIds.includes(sessionId)) {
          startNewSession();
        }
      } catch (error) {
        console.error("error deleting sessions", error);
        notifyDeleteError();
      }
    },
    [deleteHistory, notifyDeleteError, sessionId, startNewSession],
  );

  const historyBulkDeleteMenu = bulkDelete.showCheckboxes
    ? []
    : [
        {
          key: "bulk-delete",
          label: "Bulk delete",
          icon: <DeleteIcon className="h-4 w-4" />,
        },
      ];

  const historyBulkDelete = {
    showCheckboxes: bulkDelete.showCheckboxes,
    setShowCheckboxes: bulkDelete.setShowCheckboxes,
    checkedList: bulkDelete.checkedList,
    setCheckedList: bulkDelete.setCheckedList,
    confirmingBulk: bulkDelete.confirmingBulk,
    setConfirmingBulk: bulkDelete.setConfirmingBulk,
    onDeleteMultiple: handleDeleteMultipleHistoryItems,
  };

  const isSidePanelOpen = panel.type !== "none";
  const activeInsight =
    panel.type === "insights" ? (panel.insight ?? firstChat) : null;

  const showDeleteButton =
    !isNewSession &&
    (isAHistoryItem || (!isChatLoading && chatHistory.length > 0));

  return (
    <Layout style={{ background: "#EFF1F2", height: "89vh" }} className="m-3">
      {contextHolder}
      <Layout style={{ background: "#EFF1F2" }}>
        <Header className="bg-white p-0 rounded-t-lg border-b">
          <ChatHeader
            prompt={contextPrompt}
            isNewSession={isNewSession}
            handleViewSessionHistory={handleViewSessionHistory}
            handleIsNewSession={startNewSession}
            oldSessionTimeStamp={oldSessionTimeStamp}
            isSidePanelOpen={isSidePanelOpen}
            isHistoryItem={showDeleteButton}
            onDeleteSession={() => {
              if (dontAskAgain) handleDeleteCurrentSession();
              else setConfirmingDelete(true);
            }}
          />
        </Header>
        <Content className=" bg-white px-5 overflow-scroll hide-scrollbar">
          {isNewSession ? (
            <ChatCover handleSuggestedQuestions={handleSuggestedQuestions} />
          ) : (
            <ChatResults
              isLoading={isChatLoading}
              chatHistory={chatHistory}
              handleViewSearchSources={handleViewSearchSources}
              handleRegenerate={handleRegenerate}
              viewSourcesOpenId={panel.type === "sources" ? panel.chatId : null}
              handleOpenCreateInsights={handleOpenCreateInsights}
              isAHistoryItem={isAHistoryItem}
              buttonSize="middle"
            />
          )}
        </Content>
        <Footer className="rounded-b-lg shadow-lg bg-white p-5">
          <ChatInput
            isNewSession={isNewSession}
            handleSearch={handleSearch}
            isLoading={isChatLoading}
          />
          <div className="flex flex-col mt-2 justify-between items-center">
            <p className="text-[#556C75] text-xs">
              Verify reasoning with provided explanation. Always follow
              <Button
                type="text"
                className="text-[#556C75] text-xs underline p-0 ml-1"
                onClick={handleOpenModal}
              >
                Ask Vivo User Guidelines
              </Button>
              .
            </p>
          </div>
        </Footer>
      </Layout>
      {panel.type === "history" && (
        <Sider
          width="30%"
          className="rounded-lg shadow-lg"
          style={{
            background: "white",
            marginLeft: "10px",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <SidePanelShell
            title="History"
            onClose={handleCloseSessionHistory}
            actions={
              historyBulkDeleteMenu.length > 0 ? (
                <Dropdown
                  menu={{
                    items: historyBulkDeleteMenu,
                    onClick: ({ key }: { key: string | number }): void => {
                      if (key === "bulk-delete") {
                        bulkDelete.openBulkDelete();
                      }
                    },
                  }}
                  placement="bottomLeft"
                  trigger={["click"]}
                  arrow={false}
                >
                  <Button
                    type="text"
                    aria-label="History actions"
                    className="text-[#556C75] px-2 text-base leading-none"
                  >
                    •••
                  </Button>
                </Dropdown>
              ) : null
            }
          >
            <AskVivoHistoryPanel
              isLoading={isHistoryLoading}
              error={historyError}
              historyData={historyData}
              activeSessionId={firstChat?.session_id ?? sessionId}
              onSelect={handleLoadHistoryItem}
              onDelete={handleDeleteHistoryItem}
              compact
              bulkDelete={historyBulkDelete}
            />
          </SidePanelShell>
        </Sider>
      )}
      {panel.type === "sources" && (
        <Sider
          width="30%"
          className="rounded-lg shadow-lg"
          style={{
            background: "white",
            marginLeft: "10px",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <SidePanelShell
            title="Explanation"
            onClose={handleCloseSearchSources}
          >
            <ChatSources
              handleCloseSearchSources={handleCloseSearchSources}
              sources={panel.chat}
              compact
              hideHeader
            />
          </SidePanelShell>
        </Sider>
      )}
      {panel.type === "insights" && activeInsight && (
        <Sider
          width="30%"
          className="rounded-lg shadow-lg"
          style={{
            background: "white",
            marginLeft: "10px",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <SidePanelShell
            title={
              panel.step === "create"
                ? "Create Insight"
                : panel.step === "alert"
                  ? "Add Alert"
                  : "Success!"
            }
            onClose={handleCloseInsightsPanel}
          >
            <CreateInsightPanel
              handleClose={handleCloseInsightsPanel}
              insight={activeInsight}
              step={panel.step}
              hideHeader
              compact
              onStepChange={(step) =>
                openPanel({ type: "insights", insight: activeInsight, step })
              }
            />
          </SidePanelShell>
        </Sider>
      )}
      <GuidelinesModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      <AskVivoDeleteConfirmModal
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={handleDeleteCurrentSession}
        saveDontAskAgain={setDontAskAgain}
      />
    </Layout>
  );
}
